import {
  slotContainer,
  type BlockSignal,
  type BlockSpec,
  type IRNode,
  type Issue,
} from '@lp/shared';
import { compact, heading, paragraph, section, stack } from '../shared/helpers.js';
import { gridCls } from '../shared/patterns.js';

export interface FeatureGridProps extends Record<string, unknown> {
  heading: string;
  intro: string;
  columns: '2' | '3' | '4';
  tone: 'white' | 'surface';
  items: unknown;
}

export const FeatureGrid: BlockSpec<FeatureGridProps> = {
  id: 'FeatureGrid',
  label: 'Feature grid',
  category: 'landing',
  description: 'A heading and a grid of feature cards.',

  fields: {
    heading: { type: 'text', label: 'Heading (H2)', maxChars: 70, lint: 'heading' },
    intro: { type: 'textarea', label: 'Intro', maxChars: 200, lint: 'body' },
    columns: {
      type: 'radio',
      label: 'Columns',
      options: [
        { label: 'Two', value: '2' },
        { label: 'Three', value: '3' },
        { label: 'Four', value: '4' },
      ],
    },
    tone: {
      type: 'radio',
      label: 'Background',
      options: [
        { label: 'White', value: 'white' },
        { label: 'Soft grey', value: 'surface' },
      ],
    },
    items: { type: 'slot', label: 'Cards', allow: ['FeatureCard'], min: 2, max: 8 },
  },

  defaultProps: {
    heading: 'Why organisers use Collctiv',
    intro: '',
    columns: '3',
    tone: 'surface',
    items: [],
  },

  toIR(props): IRNode {
    const { tone } = props;

    const cols = gridCls(props.columns, '4');

    const header = compact([
      heading(2, props.heading, tone, true),
      paragraph('lp-lead', props.intro, tone, true),
    ]);

    return section(
      tone,
      compact([
        // `tight` because a heading and its intro are one unit — the default stack gap
        // reads as a gulf between them.
        header.length
          ? stack(header, { tight: true, centred: true, displayName: 'Section header' })
          : null,
        slotContainer('items', 'Grid', 'div', cols, 'Cards'),
      ]),
      { displayName: 'Feature grid' },
    );
  },

  signals(props): BlockSignal[] {
    const out: BlockSignal[] = [];
    if (props.heading) {
      out.push({ kind: 'heading', level: 2, text: props.heading });
      out.push({ kind: 'text', fieldPath: 'heading', value: props.heading, scope: 'heading' });
    }
    if (props.intro) out.push({ kind: 'text', fieldPath: 'intro', value: props.intro, scope: 'body' });
    return out;
  },

  validate(props): Issue[] {
    // Puck's slot field has no min/max of its own, so cardinality is enforced here.
    const count = Array.isArray(props.items) ? props.items.length : 0;
    if (count === 0) {
      return [
        {
          id: 'grid-empty',
          severity: 'warn',
          message: 'This grid has no cards yet.',
          location: { fieldPath: 'items' },
        },
      ];
    }
    if (count === 1) {
      return [
        {
          id: 'grid-one-card',
          severity: 'info',
          message: 'A grid with one card reads oddly. Add another, or use a rich text section.',
          location: { fieldPath: 'items' },
        },
      ];
    }
    return [];
  },
};
